import type { Buffer } from 'node:buffer'
import type { ParsedEvent, ReconnectInterval } from 'eventsource-parser'

import { AskStreamEventId, type AskStreamEventText } from '../ask'
import { AskStreamError } from '../errors'
import { buildUrl } from '../utils/build-url'
import { fetchThrowsIfNotOk } from '../utils/fetch-throws-if-not-ok'
import { fetchSSE } from '../utils/fetchSSE'

export type EnrichedTextChunkImage = {
  url: string
  alt: string
}

export type EnrichedTextChunkContentMetadata = {
  h1?: string
  h2?: string
  h3?: string
  h4?: string
  h5?: string
  images: EnrichedTextChunkImage[]
}

export type EnrichedTextChunkFragment = {
  content: string
  metadata: EnrichedTextChunkContentMetadata
}
export type EnrichedTextChunksResult = { chunks: EnrichedTextChunkFragment[] }

export type NER = {
  entity_group: string
  score: number
  word: string
  start: number
  end: number
}

export type LangResult = { lang: string; score: number }[]

type LLMToolCall = {
  id: string
  name: string
  arguments: string
  // Provider reasoning signature (Gemini 3.x thought signature), carried on the
  // assistant message so it can be echoed back to keep the thinking thread.
  thoughtSignature?: string
}

type LLMToolReference = {
  tool_name: string
}

type LLMToolResult = {
  id: string
  name: string
  output: string
  /** Tool references to include in result - signals deferred tool loading */
  toolReferences?: LLMToolReference[]
}

export type LLMImageUpload = {
  name: string
  buffer: Buffer
  mediaType: string
}

export type LLMUserMessage = {
  role: 'user'
  content: string
  toolResults?: LLMToolResult[]
  files?: LLMImageUpload[]
  cache_control?: { type: string }
}

export type LLMToolResultMessage = LLMUserMessage & {
  toolResults: LLMToolResult[]
}

export type LLMSystemMessage = {
  role: 'system'
  content: string
  cache_control?: { type: string }
}

export type LLMThinkingBlock =
  | { type: 'thinking'; thinking: string; signature: string }
  | { type: 'redacted_thinking'; data: string }

export type LLMAssistantMessage = {
  role: 'assistant'
  content: string
  toolCalls?: LLMToolCall[]
  thinkingBlocks?: LLMThinkingBlock[]
  cache_control?: { type: string }
}

export type GPTMessage = {
  role: 'user' | 'system' | 'assistant'
  content: string

  cache_control?: { type: string } // claude support
}

export type LLMMessage =
  | GPTMessage // including for easier backwards compat of our client methods
  | LLMUserMessage
  | LLMSystemMessage
  | LLMAssistantMessage

export function isLLMSystemMessage(
  message: LLMMessage
): message is LLMSystemMessage {
  return message.role === 'system'
}

export function isLLMUserMessage(
  message: LLMMessage
): message is LLMUserMessage {
  return message.role === 'user'
}

export function isLLMToolResultMessage(
  message: LLMMessage
): message is LLMToolResultMessage {
  return (
    isLLMUserMessage(message) &&
    Array.isArray((message as LLMUserMessage).toolResults)
  )
}

export function isLLMAssistantMessage(
  message: LLMMessage
): message is LLMAssistantMessage {
  return message.role === 'assistant'
}

export type GPTFunction = {
  name: string
  description: string
  parameters: Record<string, unknown>
}

export type GPTFunctionCallOption =
  | 'none'
  | 'auto'
  | 'required'
  | { name: string }

type ChatEventData = { answer: string; truncated?: boolean }

export type AIPromptAnswer = { answer: string; truncated: boolean }

export async function promptAIJsonObject({
  baseUrl,
  token,
  messages,
  temperature = 0.5,
}: {
  baseUrl: string
  token: string
  messages: GPTMessage[]
  temperature?: number
}) {
  const req = await fetchThrowsIfNotOk(
    buildUrl(baseUrl, '/prompt-json-object'),
    {
      method: 'POST',
      headers: {
        'Content-type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        messages,
        temperature,
      }),
    }
  )

  const result = (await req.json()) as unknown

  return result
}

function handleChatEvents(
  data: ChatEventData | undefined,
  onData: (data: ChatEventData) => void
) {
  return function handleSseEvent(parsedEvent: ParsedEvent | ReconnectInterval) {
    if (parsedEvent.type === 'event') {
      const { event: askEventId, data: eventData } = parsedEvent
      if (!data) {
        return
      }
      if (askEventId === AskStreamEventId.Text) {
        const result: AskStreamEventText = JSON.parse(eventData)
        data.answer += result.text
        onData(data)
      } else if (askEventId === AskStreamEventId.AnswerTruncated) {
        data.truncated = true
      } else if (askEventId === AskStreamEventId.Error) {
        throw new AskStreamError(eventData)
      }
    }
  }
}

/**
 * Direct access to GPT OpenAI
 *
 * `baseUrl` is the endpoint of the search service (ex: https://domain.slite.com/search)
 *
 * `token` is the search service token
 *
 * `messages` GPTMessage format to provide system prompt and discussion between user and assistant
 *
 * `temperature` to let the model be creative
 *
 * `onData` callback to receive live answer produced token by token
 */
export async function streamAIPrompt({
  baseUrl,
  token,
  messages,
  maxAnswerTokens,
  temperature = 0.5,
  onData = () => {
    // Do nothing by default
  },
  shouldStop,
  signal,
}: {
  baseUrl: string
  token: string
  messages: GPTMessage[]
  maxAnswerTokens?: number
  temperature?: number
  onData?: (data: { answer: string }) => void
  shouldStop?: () => boolean
  signal?: AbortSignal
}): Promise<AIPromptAnswer> {
  const data: ChatEventData = {
    answer: '',
  }

  await fetchSSE<ChatEventData>({
    fetchInput: buildUrl(baseUrl, '/prompt'),
    fetchInit: {
      signal,
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
      body: JSON.stringify({
        messages,
        temperature,
        ...(maxAnswerTokens ? { maxAnswerTokens } : {}),
      }),
    },
    data,
    onData,
    sseEventHandler: handleChatEvents,
    shouldStop,
  })
  return { answer: data.answer, truncated: data.truncated ?? false }
}

// endpoint for AI editor features (quick improve, grammar, translate, etc.)
export async function promptAIEditor({
  baseUrl,
  token,
  task,
  xml,
  language,
  freeformPrompt,
  format,
  onData = () => {
    // noop
  },
  signal,
}: {
  baseUrl: string
  token: string
  task: string
  xml: string
  language?: string | null
  freeformPrompt?: string | null
  format?: 'sliteml' | 'hybrid'
  onData?: (data: { answer: string }) => void
  signal?: AbortSignal
}) {
  const data: { answer: string } = {
    answer: '',
  }

  await fetchSSE<{ answer: string }>({
    fetchInput: buildUrl(baseUrl, '/ai-editor-prompt'),
    fetchInit: {
      signal,
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
      body: JSON.stringify({
        task,
        xml,
        language: language ?? null,
        freeformPrompt: freeformPrompt ?? null,
        ...(format && { format }),
      }),
    },
    data,
    onData,
    sseEventHandler: handleChatEvents,
  })
  return data.answer
}

export async function langDetector({
  baseUrl,
  token,
  text,
  signal,
}: {
  baseUrl: string
  token: string
  text: string
  signal?: AbortSignal
}) {
  const req = await fetchThrowsIfNotOk(buildUrl(baseUrl, '/lang-detector'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ text }),
    signal,
  })

  const result = (await req.json()) as LangResult

  return result
}

export * from './fetch-agent-kernel'
export * from './fetch-super'
export * from './fetch-workflow-generator'
